import React from 'react';

export interface ResponsiveImageProps
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'srcSet' | 'loading' | 'fetchPriority'
  > {
  urls:
    | {
        /** Thumbnail variant (245w). Pass null or undefined to omit from srcSet. */
        thumb: string | null | undefined;
        default: string;
        /** 2× variant (980w). Pass null or undefined to omit from srcSet. */
        _2x: string | null | undefined;
      }
    | {
        /** Short form: default-only (no thumb or 2x). */
        default: string;
      };
  sizes: string;
  /** Intrinsic dimensions for CLS prevention. Defaults to card aspect (490x684). */
  width?: number;
  height?: number;
  /** Image loading hint. Defaults to `lazy`. Pass `eager` for above-the-fold images. */
  loading?: 'lazy' | 'eager';
  /** Resource hint priority. Pass `high` for LCP candidates. React 18.3 lowercases this to the `fetchpriority` HTML attribute. */
  fetchPriority?: 'high' | 'low' | 'auto';
}

/**
 * Shared responsive-image adapter. Emits <img srcSet sizes loading="lazy"
 * decoding="async" width height> with fallback to the default URL when
 * thumb/2x variants are missing.
 *
 * The `loading` and `fetchPriority` props are surfaced so callers can mark
 * above-the-fold images as eager + high-priority for LCP.
 */
export function ResponsiveImage({
  urls,
  sizes,
  alt,
  loading = 'lazy',
  fetchPriority,
  decoding = 'async',
  width = 490,
  height = 684,
  ...rest
}: ResponsiveImageProps): JSX.Element {
  const thumb = 'thumb' in urls ? urls.thumb : undefined;
  const _2x = '_2x' in urls ? urls._2x : undefined;

  const srcSetEntries = [
    thumb ? `${thumb} 245w` : null,
    `${urls.default} 490w`,
    _2x ? `${_2x} 980w` : null,
  ].filter((x): x is string => x !== null);

  return (
    <img
      src={urls.default}
      srcSet={srcSetEntries.join(', ')}
      sizes={sizes}
      alt={alt}
      loading={loading}
      {...(fetchPriority !== undefined ? { fetchPriority } : {})}
      decoding={decoding}
      width={width}
      height={height}
      {...rest}
    />
  );
}
